import type { DockviewApi, IDockviewDefaultTabProps } from 'dockview';
import { isFunction } from 'lodash-es';
import type { LayoutConstraints, PanelType } from '../../../types/LayoutConfig';

/** Apply min and max width constraints and default size until Dockview adds support in the next version */
export function applyLayoutConstraints(constraints: LayoutConstraints[], api: DockviewApi) {
  constraints.forEach(constraint => {
    const targettedPanels = api.panels.filter(
      p => p.group.id === constraint.groupId || constraint.panelIds.includes(p.id)
    );
    targettedPanels.forEach(panel => {
      if (constraint.maxWidth || constraint.minWidth) {
        panel.group.api.setConstraints({
          maximumWidth: constraint.maxWidth,
          minimumWidth: constraint.minWidth,
        });
      }
    });
  });
}

const ENUMERATED_INDEX = /^\d+$/;

function isDuplicateOrEnumeratedTitle(title: string, preferredTitle: string): boolean {
  if (title === preferredTitle) {
    return true;
  }
  const prefix = `${preferredTitle} (`;
  if (!title.startsWith(prefix) || !title.endsWith(')')) {
    return false;
  }
  return ENUMERATED_INDEX.test(title.slice(prefix.length, -1));
}

/** Adds (1), (2) etc. to avoid duplicated titles */
export function getEnumeratedTitle(preferredTitle: string, currentPanels: { title?: string }[]): string {
  const duplicatePanel = currentPanels.filter(
    panel => panel.title && isDuplicateOrEnumeratedTitle(panel.title, preferredTitle)
  );
  if (duplicatePanel.length === 0) {
    return preferredTitle;
  } else {
    // find next panel with available number
    let nextNumber = 2;
    const isDuplicate = (panel: { title?: string }) => panel.title === `${preferredTitle} (${nextNumber})`;
    while (duplicatePanel.some(isDuplicate)) {
      nextNumber++;
    }
    return `${preferredTitle} (${nextNumber})`;
  }
}

const DEFAULT_REMOVE_TAB_MESSAGE = (title: string) => `You are about to remove the "${title}" tab.`;
const REMOVE_TAB_MESSAGE: Partial<Record<PanelType, (title: string) => string>> = {
  marketCards: title => `You are about to remove the "${title}" tab and the associated market cards.`,
};

export const getRemoveTabMessage = (title: string, type: PanelType | string): string => {
  const messageGetter = REMOVE_TAB_MESSAGE[type as PanelType];
  return isFunction(messageGetter) ? messageGetter(title) : DEFAULT_REMOVE_TAB_MESSAGE(title);
};

export const closeTabText = (panel: IDockviewDefaultTabProps): string => {
  const type = panel.api.component as keyof typeof REMOVE_TAB_MESSAGE;
  return getRemoveTabMessage(panel.api.title ?? 'this', type);
};
